import React, { useEffect, useMemo, useState } from "react";
import { Search, Eye, EyeOff, GraduationCap, ChevronDown, ChevronRight, Info } from "lucide-react";
import { apiGet, apiPost } from "../../../../Api/Client.js";
import { isJuniorType } from "../../../../Shared/ScreenUtils.jsx";
import { errorText } from "../../../../Api/errorText.js";

const PAGE_SIZE = 50;

function formatDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Super-admin only, reached from inside the Admin Panel. A quiet historical
// record of former people, kept for exactly one reason: if a background
// verification company asks "did this person work here," there should be an
// honest answer. Nothing here is visible to anyone outside the company on
// its own — every row starts off, and stays off until someone here
// deliberately switches it on for that one person.
export function AlumniRecordsView() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState("");
  const [certFilter, setCertFilter] = useState("");
  const [visFilter, setVisFilter] = useState("");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showVisHelp, setShowVisHelp] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (certFilter) p.set("certificate_status", certFilter);
    if (visFilter) p.set("portal_visible", visFilter);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(page * PAGE_SIZE));
    return p.toString();
  }, [q, certFilter, visFilter, page]);

  // Any filter change starts back at page 1 — otherwise a narrower search
  // can land on a page past the end of its own results.
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [q, certFilter, visFilter]);

  const load = () => {
    setLoading(true);
    setLoadError("");
    Promise.all([
      apiGet("/Alumni/summary/"),
      apiGet(`/Alumni/records/?${params}`),
    ]).then(([s, r]) => {
      setSummary(s);
      setRows(r.results || []);
      setCount(r.count || 0);
    }).catch((err) => {
      // A failed load must not read as "no alumni exist".
      setRows([]);
      setCount(0);
      setLoadError(errorText(err, "The alumni records could not be loaded."));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const toggle = async (row) => {
    setBusyId(row.id);
    try {
      const updated = await apiPost(`/Alumni/records/${row.id}/toggle/`, {});
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      setSummary((s) => s && ({ ...s, portal_visible: s.portal_visible + (updated.portal_visible ? 1 : -1) }));
    } catch (e) {
      alert(e?.payload?.detail || e?.message || "Could not change visibility.");
    } finally {
      setBusyId(null);
    }
  };

  const bulkToggle = async (makeVisible) => {
    const label = makeVisible ? "make visible" : "hide";
    if (!window.confirm(`${label === "hide" ? "Hide" : "Make visible"} every record matching the current filters (${count})?`)) return;
    try {
      await apiPost("/Alumni/records/bulk-toggle/", {
        q: q.trim() || undefined,
        certificate_status: certFilter || undefined,
        // The "Visible or not" filter narrows the change to the rows currently shown / hidden.
        visibility_filter: visFilter || undefined,
        // No filter chosen means "every record"; the server needs that said out loud.
        all: !q.trim() && !certFilter && !visFilter ? true : undefined,
        portal_visible: makeVisible,
      });
      load();
    } catch (e) {
      alert(e?.payload?.detail || e?.message || "Bulk update failed.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="AdmX-Alumni">
      <div className="AdmX-Alumni-Head">
        <h2><GraduationCap size={19} /> Alumni Records</h2>
        <p>
          People who worked here before August 2026 — before intranet-v2 existed. Kept so a
          background-verification request has a real answer, not "we don't know."
        </p>
      </div>

      {summary && (
        <div className="AdmX-Alumni-Summary">
          <div><strong>{summary.total}</strong><span>total records</span></div>
          <div><strong>{summary.portal_visible}</strong><span>visible</span></div>
          <div><strong>{summary.with_certificate}</strong><span>with a certificate on record</span></div>
          <div><strong>{summary.full_time_no_certificate}</strong><span>full-time, no certificate on record</span></div>
        </div>
      )}

      <div className="AdmX-Alumni-Filters">
        <div className="AdmX-Alumni-Search">
          <Search size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or ALM- code…" />
        </div>
        <select value={certFilter} onChange={(e) => setCertFilter(e.target.value)}>
          <option value="">Any certificate status</option>
          <option value="found">Certificate on record</option>
          <option value="none">No certificate record</option>
          <option value="unknown">Unknown</option>
        </select>
        <select value={visFilter} onChange={(e) => setVisFilter(e.target.value)}>
          <option value="">Visible or not</option>
          <option value="true">Visible only</option>
          <option value="false">Hidden only</option>
        </select>
        <button type="button" className="AdmX-Alumni-Bulk" onClick={() => bulkToggle(true)}>Make matching visible</button>
        <button type="button" className="AdmX-Alumni-Bulk ghost" onClick={() => bulkToggle(false)}>Hide matching</button>
      </div>

      <div className="AdmX-Alumni-VisHelp">
        <button type="button" className="AdmX-Alumni-VisHelp-Btn" onClick={() => setShowVisHelp((v) => !v)}>
          <Info size={13} /> What does Visible / Hidden mean?
        </button>
        {showVisHelp && (
          <p>
            Every record starts <strong>Hidden</strong> — that's the whole point of this table: nothing
            in it can be seen by anyone outside the company yet. <strong>Visible</strong> is a switch for
            later, once the self-serve verification portal exists — it marks which specific people are
            allowed to show up there. Nothing is public right now regardless of this switch; the portal
            it controls hasn't been built yet.
          </p>
        )}
      </div>

      {loading ? (
        <p className="AdmX-Alumni-Muted">Loading…</p>
      ) : loadError ? (
        <p className="AdmX-Alumni-Muted" role="alert">{loadError}</p>
      ) : rows.length === 0 ? (
        <p className="AdmX-Alumni-Muted">No records match.</p>
      ) : (
        <div className="AdmX-Alumni-Table">
          <div className="AdmX-Alumni-Row AdmX-Alumni-Row-Head">
            <span /><span>Name</span><span>Email</span><span>Started</span><span>Planned end</span>
            <span>Certificate</span><span>Visible</span>
          </div>
          {rows.map((r) => {
            const isFullTime = !isJuniorType(r.employment_type);
            const isOpen = expandedId === r.id;
            return (
              <React.Fragment key={r.id}>
                <button type="button" className="AdmX-Alumni-Row AdmX-Alumni-Row-Click" onClick={() => setExpandedId(isOpen ? null : r.id)}>
                  <span className="AdmX-Alumni-Expand">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                  <span className="name">
                    {r.full_name}
                    <small>{r.reference_code}{isFullTime ? " · Full-time" : ""}</small>
                  </span>
                  <span>{r.email}</span>
                  <span>{r.started_on || "—"}</span>
                  <span>{r.planned_end_on || "—"}</span>
                  <span className={`cert cert-${r.certificate_status}`}>
                    {r.certificate_status === "found" ? "On record" : r.certificate_status === "none" ? "No record" : "Unknown"}
                  </span>
                  <span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={"AdmX-Alumni-Toggle" + (r.portal_visible ? " on" : "")}
                      aria-disabled={busyId === r.id}
                      onClick={(e) => { e.stopPropagation(); if (busyId !== r.id) toggle(r); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); if (busyId !== r.id) toggle(r); } }}
                      title={r.portal_visible ? "Visible — click to hide" : "Hidden — click to make visible"}
                    >
                      {r.portal_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                      {r.portal_visible ? "Visible" : "Hidden"}
                    </span>
                  </span>
                </button>
                {isOpen && (
                  <div className="AdmX-Alumni-Detail">
                    <div>
                      <strong>Certificate</strong>
                      {r.certificate_status === "found" ? (
                        <>
                          <p>Logged as sent{r.certificate_last_sent_on ? ` on ${r.certificate_last_sent_on}` : ""}, for: {(r.certificate_positions || []).filter(Boolean).join(", ") || "role not recorded"}.</p>
                          <p className="muted">No certificate file exists in the legacy data for anyone — this is a log entry, not a document. There's nothing to open.</p>
                        </>
                      ) : (
                        <p className="muted">No certificate log entry for this person. The legacy log never covered everyone who genuinely worked here — this alone isn't proof one was never issued.</p>
                      )}
                    </div>
                    <div>
                      <strong>Best signal of when they were last around</strong>
                      <p>Last logged in: {formatDateTime(r.last_login_at)}</p>
                      <p>Last processed payment: {r.last_payment_processed_month ? `${MONTHS[r.last_payment_processed_month]} ${r.last_payment_processed_year}` : "no payment record found"}</p>
                      <p className="muted">Neither is a confirmed last working day — the legacy system never recorded one. These are the closest real signals it has.</p>
                    </div>
                    <div>
                      <strong>Other details on file</strong>
                      <p>{r.title || "Role not recorded"} {r.legacy_department_name ? `· ${r.legacy_department_name}` : ""}</p>
                      <p>{r.phone || "No phone"}{r.whatsapp_contact && r.whatsapp_contact !== r.phone ? ` · WhatsApp ${r.whatsapp_contact}` : ""}</p>
                      <p>{r.github_username ? `GitHub: ${r.github_username}` : "No GitHub on file"}</p>
                      <p>{r.slack_username ? `Slack: ${r.slack_username}` : "No Slack on file"}</p>
                      <p>{r.college ? `College: ${r.college}` : ""}</p>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {count > 0 && (
        <div className="AdmX-Alumni-Pager">
          <span>{page * PAGE_SIZE + 1}–{Math.min(count, (page + 1) * PAGE_SIZE)} of {count}</span>
          <div>
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span>Page {page + 1} of {totalPages}</span>
            <button type="button" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
